import { isAxiosError } from 'axios';
import { ApiError, AppError, ErrorCode } from '@/types/ErrorCodes';
import { formatDistance } from 'date-fns';
import {
	Language,
	Translations,
	Location,
	LOCALIZATION_TYPES,
} from '@/types/types';
import { enUS } from 'date-fns/locale';
import APP_CONFIG from '@/configs/appConfig';
import LANGUAGES from '@/configs/languages';
import moment from 'moment';

export const priceFormatter = new Intl.NumberFormat('en-GB', {
	style: 'currency',
	currency: 'PLN',
	currencyDisplay: 'code',
});

const PASSWORD_REGEX = /^(?=\D*\d)[^ ]{6,}$/;

/**
 *
 * @param password one number and at least 8
 * @returns
 */
export function validatePassword(password: string) {
	return PASSWORD_REGEX.test(password);
}

export function formatDateForPresentation(
	date: moment.MomentInput = new Date()
) {
	return moment(date).format('DD/MM/YYYY');
}

export function formatTimeForPresentation(
	date: moment.MomentInput = new Date()
) {
	return moment(date).format('HH:mm');
}

export function formatExactDate(date: moment.MomentInput = new Date()) {
	return `${formatDateForPresentation(date)}, ${formatTimeForPresentation(
		date
	)}`;
}

export function sortByDate(
	dateA: moment.MomentInput,
	dateB: moment.MomentInput,
	order: 'asc' | 'desc' = 'asc'
) {
	const first = moment(dateA).valueOf(),
		second = moment(dateB).valueOf();

	return order === 'asc' ? first - second : second - first;
}

export function sortByName(
	nameA = '',
	nameB = '',
	order: 'asc' | 'desc' = 'asc'
) {
	return order === 'asc'
		? nameA.localeCompare(nameB)
		: nameB.localeCompare(nameA);
}

export function handleUnload(e: BeforeUnloadEvent) {
	e.preventDefault();
}

export function fileTypeToHeader(fileType: 'csv' | 'xlsx' | 'pdf') {
	switch (fileType) {
		case 'xlsx':
			return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
		case 'csv':
			return 'text/csv';
		case 'pdf':
			return 'application/pdf';
		default:
			return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
	}
}

export const formatPrice = (value = 0, opts?: Intl.NumberFormatOptions) => {
	const formatter = new Intl.NumberFormat('en-GB', {
		style: 'currency',
		currency: 'PLN',
		currencyDisplay: 'code',
		maximumFractionDigits: !value ? 0 : undefined,
		...opts,
	});

	return formatter.format(value);
};

export const isAppError = (error: unknown): error is AppError => {
	return (
		!!(error as AppError).status &&
		!!(error as AppError).stack &&
		!!(error as AppError).message
	);
};

export const getErrorCodes = (error: unknown) => {
	if (isAxiosError<ApiError>(error) && error.response)
		return Array.isArray(error.response.data.message)
			? error.response.data.message
			: [error.response.data.message];

	return [ErrorCode.GENERIC];
};

export const errorCodesToMessage = (errorCodes: ErrorCode[]) =>
	errorCodes.map((code) => `errors.${code}`).join(', ');

export const fontColorForBackground = (backgroundColorHex = '#ffffff') => {
	return [
		0.299 * parseInt(backgroundColorHex.slice(1, 3), 16),
		0.587 * parseInt(backgroundColorHex.slice(3, 5), 16),
		0.114 * parseInt(backgroundColorHex.slice(5, 7), 16),
	].reduce((a, b) => a + b) /
		255 >
		0.65
		? '#495057'
		: '#ffffff';
};

export const formatFullName = (firstName?: string, lastName?: string) => {
	if (firstName && lastName) return `${firstName} ${lastName}`;
	if (!firstName && !lastName) return `-`;
	return firstName || lastName;
};

export function getFileSrc(path?: string) {
	return `${import.meta.env.VITE_API_URL_UPLOADS}${path}`;
}

export function formatDateFromNow(date: Date) {
	return formatDistance(date, new Date(), {
		locale: enUS,
		includeSeconds: true,
		addSuffix: true,
	});
}

export function isDefaultLanguage(language: Language): language is 'EN' {
	return language === APP_CONFIG.DEFAULT_LANGUAGE;
}

export function fileSizeToMB(size: number | string) {
	return Math.round((Number(size) / 1000000 + Number.EPSILON) * 100) / 100;
}

export function getEmptyTranslationObject<K extends string>(
	keys: K[]
): Translations<Record<K, string>> {
	const translations = {} as Translations<Record<K, string>>;

	let language: Language;
	for (language in LANGUAGES) {
		if (language === APP_CONFIG.DEFAULT_LANGUAGE) continue;

		translations[language] = {} as Record<K, string>;

		for (const key of keys) {
			translations[language][key] = '';
		}
	}

	return translations;
}

const displayNameAsString = (
	displayName: google.maps.places.Place['displayName']
): string | null => {
	if (!displayName) return null;
	if (typeof displayName === 'string') return displayName;
	const text = (displayName as { text?: string }).text;
	return text ?? null;
};

function localityPlaceIdFromGeocoderResults(
	results: google.maps.GeocoderResult[] | null
) {
	if (!results?.length) return null;
	for (const r of results)
		if (r.types.includes(LOCALIZATION_TYPES.CITY) && r.place_id)
			return r.place_id;
	return null;
}

async function resolveCityGooglePlaceId(args: {
	locationLiteral: google.maps.LatLngLiteral | null;
	cityName: string;
	provinceName: string;
}) {
	if (typeof google === 'undefined') return null;

	const geocoder = new google.maps.Geocoder();

	if (args.locationLiteral) {
		const fromCoords = await new Promise<google.maps.GeocoderResult[] | null>(
			(resolve) => {
				geocoder.geocode({ location: args.locationLiteral }, (res, status) => {
					if (status !== 'OK' || !res) {
						resolve(null);
						return;
					}
					resolve(res);
				});
			}
		);
		const fromLocality = localityPlaceIdFromGeocoderResults(fromCoords);
		if (fromLocality) return fromLocality;
	}

	if (!args.cityName) return null;

	const addressLine = [args.cityName, args.provinceName, 'Polska']
		.filter(Boolean)
		.join(', ');

	const fromAddress = await new Promise<google.maps.GeocoderResult[] | null>(
		(resolve) => {
			geocoder.geocode(
				{
					address: addressLine,
					region: 'pl',
					componentRestrictions: { country: 'pl' },
				},
				(res, status) => {
					if (status !== 'OK' || !res) {
						resolve(null);
						return;
					}
					resolve(res);
				}
			);
		}
	);

	return localityPlaceIdFromGeocoderResults(fromAddress);
}

function placeLocationAsLiteral(
	location: google.maps.places.Place['location']
): google.maps.LatLngLiteral | null {
	if (!location) return null;
	if (typeof location.lat === 'function')
		return { lat: location.lat(), lng: location.lng() };
	const lit = location as unknown as google.maps.LatLngLiteral;
	if (typeof lit.lat === 'number' && typeof lit.lng === 'number') return lit;
	return null;
}

export async function mapGooglePlaceToLocation(
	place: google.maps.places.Place,
	options: { cityOnly: boolean }
): Promise<Location | null> {
	if (!place.id) return null;

	const displayNameText = displayNameAsString(place.displayName);
	if (!displayNameText) return null;

	const cityComponent = place.addressComponents?.find((component) =>
		component.types.includes(LOCALIZATION_TYPES.CITY)
	);

	if (!cityComponent) return null;

	const provinceName =
			place.addressComponents?.find((component) =>
				component.types.includes(LOCALIZATION_TYPES.COUNTY)
			)?.longText || '',
		cityName = cityComponent.longText || '';

	let cityId = place.id;
	if (!options.cityOnly) {
		const resolved = await resolveCityGooglePlaceId({
			locationLiteral: placeLocationAsLiteral(place.location),
			cityName,
			provinceName,
		});
		if (resolved) cityId = resolved;
	}

	return {
		placeId: place.id,
		cityId,
		displayName: displayNameText,
		cityName: cityName,
		provinceName: provinceName,
		formattedAddress: place.formattedAddress ?? '',
		googleMapsUri: place.googleMapsURI ?? '',
	};
}
